import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import './VoicesStoryTransition.css';

const DURATION = 520;
const OPTIONS = { duration: DURATION, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' };
const DESTINATION = '.blogBanner .blogBannerImageWrapper > img.blogBannerImage';
const routePath = location => `${location.pathname}${location.search}${location.hash}`;
const routeKey = location => `${location.key}:${routePath(location)}`;
let activeCancel;

function visibleRect(element) {
  const rect = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  return element.isConnected && style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0'
    && [rect.left, rect.top, rect.width, rect.height].every(Number.isFinite)
    && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight
    && rect.right > 0 && rect.left < window.innerWidth ? rect : null;
}

function box(rect, radius) {
  return { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, borderRadius: radius };
}

function copyPhoto(original) {
  const photo = document.createElement('img');
  // Pin the already selected bitmap, not srcset, so resizing the overlay cannot switch it.
  photo.src = original.currentSrc || original.src;
  photo.alt = '';
  const style = getComputedStyle(original);
  Object.assign(photo.style, {
    objectFit: style.objectFit, objectPosition: style.objectPosition,
    transform: style.transform, transformOrigin: style.transformOrigin, filter: style.filter,
  });
  return photo;
}

function beginTransfer(source, frame, rect, target, navigator, media) {
  activeCancel?.();
  const overlay = document.createElement('div');
  overlay.className = 'voices-story-transfer';
  overlay.setAttribute('aria-hidden', 'true');
  const animations = [];
  const sourceRoute = routeKey(navigator.location);
  let destinationRoute;
  let destination;
  let animationFrame;
  let timeout;
  let cancelled = false;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(animationFrame);
    clearTimeout(timeout);
    window.removeEventListener('resize', cancel);
    window.removeEventListener('pagehide', cancel);
    window.removeEventListener('error', pageError, true);
    media?.removeEventListener?.('change', motionChange);
    overlay.removeEventListener('error', cancel, true);
    animations.forEach(animation => animation.cancel());
    overlay.remove();
    if (activeCancel === cancel) activeCancel = null;
  };
  const pageError = event => {
    if (event.target === window || event.target?.matches?.(DESTINATION)) cancel();
  };
  const motionChange = () => { if (media.matches) cancel(); };
  const isDestination = () => routePath(navigator.location) === target
    && (!destinationRoute || routeKey(navigator.location) === destinationRoute);
  const animate = (element, frames) => {
    const animation = element.animate(frames, OPTIONS);
    animations.push(animation);
    // Handle every cancellation/rejection, including the image and shade animations.
    animation.finished.catch(cancel);
    return animation;
  };
  activeCancel = cancel;

  try {
    const start = box(rect, getComputedStyle(frame).borderRadius);
    Object.assign(overlay.style, start);
    const photo = copyPhoto(source);
    overlay.append(photo);
    let shade;
    if (frame.matches('.vj-photo, .vj-proof')) {
      const style = getComputedStyle(frame, '::after');
      shade = document.createElement('div');
      shade.className = 'voices-story-transfer-shade';
      Object.assign(shade.style, { backgroundColor: style.backgroundColor, backgroundImage: style.backgroundImage });
      overlay.append(shade);
    }
    overlay.addEventListener('error', cancel, true);
    document.body.append(overlay);
    window.addEventListener('resize', cancel);
    window.addEventListener('pagehide', cancel);
    window.addEventListener('error', pageError, true);
    media?.addEventListener?.('change', motionChange);
    // Bound delayed/missing images and background tabs, independently of requestAnimationFrame.
    timeout = setTimeout(cancel, 1000);

    const tick = () => {
      if (cancelled) return;
      try {
        const current = routeKey(navigator.location);
        // Links navigate normally; cancelled clicks and any subsequent route end this transfer.
        if (current === sourceRoute || !isDestination()) { cancel(); return; }
        // The site's global smooth-scroll rule must not move the destination under the morph.
        if (!destinationRoute) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        destinationRoute = current;
        if (destination) {
          if (!destination.isConnected) { cancel(); return; }
        } else {
          // BlogBanner's wrapper is padded on approved pages. Measure the image, never the wrapper/logo.
          const image = document.querySelector(DESTINATION);
          if (!image || (image.complete && !image.naturalWidth)) { cancel(); return; }
          const endRect = image.complete && visibleRect(image);
          if (image.complete && !endRect) { cancel(); return; }
          if (endRect) {
            destination = image;
            const style = getComputedStyle(image);
            const finalPhoto = copyPhoto(image);
            finalPhoto.style.opacity = '0';
            overlay.insertBefore(finalPhoto, shade || null);
            animate(photo, [
              { objectPosition: photo.style.objectPosition, transform: photo.style.transform, filter: photo.style.filter },
              { objectPosition: style.objectPosition, transform: style.transform, filter: style.filter },
            ]);
            // Home and approved stories can use different crops/assets; reveal the actual approved bitmap.
            animate(finalPhoto, [{ opacity: 0 }, { opacity: 1 }]);
            if (shade) animate(shade, [{ opacity: 1 }, { opacity: 0 }]);
            // A cancelled animation restores the original computed visibility without touching inline styles.
            animate(image, [{ visibility: 'hidden' }, { visibility: 'hidden' }]);
            const morph = animate(overlay, [start, box(endRect, style.borderRadius)]);
            clearTimeout(timeout);
            timeout = setTimeout(cancel, DURATION + 80);
            morph.finished.then(cancel, cancel);
          }
        }
        animationFrame = requestAnimationFrame(tick);
      } catch { cancel(); }
    };
    animationFrame = requestAnimationFrame(tick);
  } catch { cancel(); }
  return { cancel, isDestination };
}

// Home owns mounting and motion overrides (null uses OS). Transfers outlive Home by reading
// BrowserRouter/MemoryRouter history.location; React Router retains ownership of its listener.
export function VoicesStoryTransition({ reducedMotion = null }) {
  const { navigator, basename } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    activeCancel?.();
    const media = reducedMotion == null ? window.matchMedia?.('(prefers-reduced-motion: reduce)') : null;
    if (reducedMotion || !navigator.location || typeof Element.prototype.animate !== 'function') return;
    let transfer;
    const capture = event => {
      if (event.defaultPrevented || event.button !== 0 || event.detail === 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const card = link?.closest('.vj-proof, .v-story');
      if (!card?.closest('.v-home') || link.hasAttribute('download')
        || (link.target && link.target.toLowerCase() !== '_self') || link.relList.contains('external')
        || link.closest('[inert], [aria-hidden="true"]') || media?.matches) return;
      try {
        const url = new URL(link.href, window.location.href);
        const path = basename === '/' ? url.pathname : url.pathname.slice(basename.length);
        if (url.origin !== window.location.origin || !/^\/customer-stories\/[^/]+\/?$/i.test(path)) return;
        transfer?.cancel();
        const source = card.querySelector('img');
        const frame = source?.closest('.vj-photo, .v-story-image, .vj-proof') || source;
        const rect = frame && visibleRect(frame);
        if (!rect || !source.complete || !source.naturalWidth || !visibleRect(source)) return;
        transfer = beginTransfer(source, frame, rect, routePath(url), navigator, media);
      } catch { transfer?.cancel(); }
    };
    document.addEventListener('click', capture, true);
    return () => {
      document.removeEventListener('click', capture, true);
      // Only the expected route handoff may outlive Home; the transfer owns its bounded cleanup.
      if (!transfer?.isDestination()) transfer?.cancel();
    };
  }, [navigator, basename, reducedMotion]);

  return null;
}

export default VoicesStoryTransition;
